import * as Sentry from '@sentry/react';
import { Box, Button, createStyles, makeStyles, Snackbar, Theme, Grid } from '@material-ui/core';
import React from 'react';
import { StyledSettingsContainer } from '../common/_components';
import { LineupApiResponse } from './queries/queryTypes';
import { STATUS_POLL_TIMEOUT_MS, useGetLambdaStatusForContest } from './queries/settingsQueries';
import { getSimResults, setContest, setDisableProgressCheck, setIsParsingCsv, setLineupFile, setLineupVerified, setPercentToFirst, setResultsLoadError, setShowCachedResultError, setSimCountAction, setSimulatedSlate, setTotalEntries } from './store/actions';
import { setAuth } from '../common/store/actions';
import { connect, useDispatch } from 'react-redux';
import { AuthResponse, Filters, Pages, ResultsLoadError } from './store/types';
import Upload from './upload';
import { Alert } from '@material-ui/lab';
import { APIState } from '../utils/api/reducer';
import { APIConstants } from '../utils/api/constants';
import { useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { downloadCsvFile, getFilename } from "../common/helpers/FilenameHelper";
import GetAppIcon from '@material-ui/icons/GetApp';
import { Input, Stack } from '@mui/material';
import { stackSports } from '../utils/stackConstants';
import { useUserId } from '../utils/authentication';
import moment from 'moment';
import { useGetSlateInfoQuery, useGetSlates } from '../common/queries/slateQueries';
import { SelectInput, percentToFirstOptions } from '../common/selectOptions';
import { SportsResponse, SitesResponse, SlateResponse } from '../common/store/types';
import SettingsSelectors from '../common/SettingsSelectors';
import NoSlatesView from '../common/NoSlatesView';
import UnauthenticatedView from '../common/UnauthenticatedView';
import { SettingsLoader } from '../common/SettingsLoader';
import EntryManager from './EntryManager';
import { toolTipText } from '../common/CustomToolTip';
import { filterSlates, getSupportedSports } from '../common/helpers/SelectorHelpers';
import { userSubscribed } from '../utils/authentication';

const useStyles = makeStyles((theme: Theme) => createStyles({
  formWrapper: {
    width: 200,
    color: 'white',
    marginTop: 18,
    margin: '0 12px',
    '& .MuiInputBase-root': {
      color: 'white',
      backgroundColor: '#000A16',
      borderRadius: 3,
      padding: 8,
      border: `1px solid rgba(238, 238, 238, .2)`,
      marginBottom: 4,
    },
    '& .MuiFormLabel-root': {
      color: 'white',
      marginBottom: 2,
    },
    '& .MuiSvgIcon-root': {
      color: 'white',
    },
    '& button': {
      backgroundColor: '#0042C1 !important',
      padding: 8,
    },
    '& .Mui-disabled': {
      backgroundColor: '#555555 !important',
      color: '#BEBEBE'
    },
    '& .MuiSelect-select.MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  subtitle: {
    lineHeight: '130%',
    opacity: '70%',
    fontFamily: "Inter",
  },
  advancedButton: {
    background: '#0042C1',
    width: '100%',
    color: 'white',
    fontFamily: "Inter",
  },
  signInButtonWrapper: {
    padding: 8,
  },
  signInButton: {
    width: '100%',
    border: '1px solid #0042C1',
    color: 'white',
    fontFamily: "Inter",
  },
  button: {
    color: theme.palette.common.white,
    width: 200,
    minWidth: 120,
    borderRadius: 3,
    fontSize: 14,
    fontWeight: 700,
    padding: 2,
    margin: '8px 0',
    textTransform: 'initial',
    '& a': {
      color: 'white !important',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
    },
  },
  loading: {
    animationName: '$spin',
    animationDuration: '5000ms',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
  },
  '@keyframes spin': {
    '0%': {
      transform: 'rotate(0deg)'
    },
    '100%': {
      transform: 'rotate(360deg)',
    }
  },
})
);

interface ReduxProps {
  readonly selectedSport: SportsResponse;
  readonly selectedSite: SitesResponse;
  readonly selectedType: string;
  readonly selectedSlate: SlateResponse;
  readonly selectedUser: string,
  readonly selectedOpponent: string,
  readonly filters: Filters,
  readonly pageInfo: Pages,
  readonly authenticated: APIState<AuthResponse>;
  readonly disableProgressCheck: boolean,
  readonly supportedSites: APIState<SitesResponse[]>;
  readonly totalEntries: number;
  readonly exportableLineups: string;
  readonly lineupVerified: boolean;
  readonly simulationTimestamp: { startTime: number | null };
  readonly showCachedResultError: boolean;
}

interface DispatchProps {
  SetContest: (contest: SitesResponse) => void;
  SetSimulatedSlate: (slate: SlateResponse) => void;
  SetAuth: (league: string, token: string, appName: string) => void;
  GetSimResults: (slateId: string, user: string, filters: Filters, pageInfo: Pages, sport: string, includeStacks: boolean) => void;
  SetDisableProgressCheck: (newValue: boolean) => void;
  SetTotalEntries: (newValue: number) => void;
  SetPercentToFirst: (percent: number) => void;
  SetLineupFile: (file: string) => void;
  SetLineupVerified: (verified: boolean) => void;
  SetShowCachedResultError: (show: boolean) => void;
  SetResultsLoadError: (error: ResultsLoadError | null) => void;
  SetIsParsingCsv: (isParsing: boolean) => void;
}

type Props = ReduxProps & DispatchProps;

const Settings = (props: Props) => {
  const classes = useStyles();
  const [lineupFile, setLineupFile] = React.useState("");
  const [projectionsFile, setProjectionsFile] = React.useState("");
  const [lineupFileErrors, setLineupFileErrors] = React.useState([] as string[])
  const [successSnack, setSuccessSnack] = React.useState(false);
  const [useCustomProjections] = React.useState(false);
  const userId = useUserId();
  const auth = 'awe' + userId;
  const routeParams = useParams<{ league: string }>();
  const [simCount, setSimCount] = React.useState(0);

  const dispatch = useDispatch();

  const {
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate,
    filters,
    pageInfo,
    authenticated,
    disableProgressCheck,
    exportableLineups,
    lineupVerified,
    simulationTimestamp,
    showCachedResultError,
    SetLineupFile,
    SetPercentToFirst,
    SetTotalEntries,
    GetSimResults,
    SetDisableProgressCheck,
    SetLineupVerified,
    SetShowCachedResultError,
    SetResultsLoadError,
    SetIsParsingCsv
  } = props;
  const { data, isLoading: slatesLoading } = useGetSlates('CONTESTSIM', false);
  const slates = data?.slates;
  const sports = getSupportedSports('CONTESTSIM');
  const { data: progressData } = useGetLambdaStatusForContest(
    selectedSlate.slateId,
    auth,
    disableProgressCheck,
    simulationTimestamp?.startTime
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [savedLineups, setSavedLineups] = useLocalStorage<LineupApiResponse[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );
  const [percentToFirst, setPercentToFirst] = React.useState(25);
  const [disableUpdateInfo, setDisableUpdateInfo] = React.useState(false);
  const { data: updateInfo } = useGetSlateInfoQuery(selectedSlate?.slateId?.toString(), disableUpdateInfo || selectedSlate?.slateId === undefined)
  const isUserSubscribed = userSubscribed(authenticated, selectedSite, selectedSport);

  const getTemplate = async () => {
    const params = {
      slateId: selectedSlate.slateId,
    }

    const data = await fetch(`${process.env.REACT_APP_API_URL}/api/contests/getProjectionsTemplate?` + new URLSearchParams(params)).then(res => res.json()).then(res => res.join('\n') || '');

    // adding the max exposure column to the data, doing it on the front end since it's really only for precontest
    const dataWithExposureColumns = data
    .split('\n')
    .map((line, index) => {
      if (index === 0) {
        return `${line},Max Exposure`;
      }
      return `${line},100`;
    })
    .join('\n');

    const filename = getFilename(selectedSlate, `_Projections_Template`);
    downloadCsvFile(dataWithExposureColumns, filename);
  }

  const handlePercentChange = (percent: number) => {
    setPercentToFirst(percent);
    SetPercentToFirst(percent);
  };

  React.useEffect(() => {
    // if (progressData?.progress && progressData?.progress !== 1) {
    //   allowPollingTabs.current = true;
    // }
    if (progressData?.progress === -1) {
      SetDisableProgressCheck(true);
      // Show error that simulation failed
      SetShowCachedResultError(true);
    }
    if (progressData?.progress === 1 && isUserSubscribed) {
      GetSimResults(selectedSlate.slateId, auth, filters, pageInfo, selectedSlate.sport, stackSports.includes(routeParams.league?.toLowerCase()));
      // Note: disableProgressCheck is now set in tabs.tsx when lineupAllRoi data loads
      // Hide error when fresh results arrive
      SetShowCachedResultError(false);
    }
  }, [progressData, pageInfo, filters])

  const latestProgress = React.useRef<number | undefined>();
  latestProgress.current = progressData?.progress;

  // Swap the progress bar for a "check again" message once status polling gives up without the sim reporting
  // done or failed; otherwise the bar sits at 98% forever. Fires one poll interval after polling stops so the
  // last poll can land.
  React.useEffect(() => {
    const startTime = simulationTimestamp?.startTime;
    if (disableProgressCheck || !startTime) {
      return;
    }
    const timer = setTimeout(() => {
      if (latestProgress.current !== 1 && latestProgress.current !== -1) {
        SetResultsLoadError('statusTimedOut');
      }
    }, Math.max(startTime + STATUS_POLL_TIMEOUT_MS + 5000 - Date.now(), 0));
    return () => clearTimeout(timer);
  }, [disableProgressCheck, simulationTimestamp?.startTime, SetResultsLoadError]);

  React.useEffect(() => {
    setSavedLineups([]);
  }, []);

  React.useEffect(() => {
    const validateCsv = async () => {
      SetLineupVerified(false);
      if (((!useCustomProjections && lineupFile) || (useCustomProjections && lineupFile && projectionsFile)) && authenticated.status === APIConstants.SUCCEEDED && selectedSlate?.slateId) {
        const request = {
          user: auth,
          contestData: lineupFile,
          slateId: selectedSlate.slateId,
          customProjections: useCustomProjections ? projectionsFile : '',
          tier: authenticated?.data?.authTier,
        };

        SetIsParsingCsv(true);
        await fetch(`${process.env.REACT_APP_API_URL}/api/contests/parsePreContestSim`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request)
        })
          .then(res => res.json()).then(res => {
            if (res.errors) {
              setLineupFileErrors([res?.errors?.contestType, res?.errors?.contestData]);
              SetLineupVerified(false);
            } else {
              SetLineupVerified(true)
              SetTotalEntries(res);
              setSuccessSnack(true);
            }
          })
          .catch(err => {
            Sentry.captureException(err);
            console.log('err', err)
          })
          .finally(() => {
            SetIsParsingCsv(false);
          });
      }
    };
    validateCsv().catch(err => {
      Sentry.captureException(err);
      console.log('err', err)
    });
  }, [lineupFile, projectionsFile, useCustomProjections, selectedSite, selectedSlate, selectedSport, authenticated])

  React.useEffect(() => {
    if (!useCustomProjections) {
      setProjectionsFile('');
    }
  }, [useCustomProjections])

  React.useEffect(() => {
    if (updateInfo && selectedSlate?.slateId?.toString()) {
      setDisableUpdateInfo(true);
    }
    else {
      setDisableUpdateInfo(false);
    }
  }, [updateInfo]);

  React.useEffect(() => {
    if (exportableLineups) {
      setLineupFile(exportableLineups);
      SetLineupFile(exportableLineups);
    }
  }, [exportableLineups])

  if (authenticated.status === APIConstants.STARTED || slatesLoading) {
    return (
      <SettingsLoader />
    )
  }

  if (!isUserSubscribed) {
    return (
      <UnauthenticatedView title='Pre-Contest Simulator' app='CONTESTSIM' backgroundColor='rgba(1, 90, 206, 0.15)' />
    )
  }

  if (!slatesLoading && (!slates || (slates && filterSlates(slates, selectedSport, selectedSite, selectedType).length === 0))) {
    return (
      <NoSlatesView title='Pre-Contest Simulator' app='CONTESTSIM' backgroundColor='rgba(1, 90, 206, 0.15)'/>
    )
  }

  return (
    <StyledSettingsContainer>
      {
        lineupFileErrors && lineupFileErrors.map(e => (
          <Snackbar
            open={lineupFileErrors.length > 0}
            autoHideDuration={4000}
            onClose={() => setLineupFileErrors([])}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Alert severity='error'>
              {e}
            </Alert>
          </Snackbar>
        ))
      }
      {
        lineupVerified && (
          <Snackbar
            open={successSnack}
            onClose={() => setSuccessSnack(false)}
            autoHideDuration={4000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            onClick={() => setSuccessSnack(false)}
          >
            <Alert severity='success'>
              File uploaded and verified successfully!
            </Alert>
          </Snackbar>
        )
      }
      {
        showCachedResultError && (
          <Snackbar
            open={showCachedResultError}
            onClose={() => SetShowCachedResultError(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity='error' onClose={() => SetShowCachedResultError(false)}>
              Simulation run failed, showing previous sim results. Check pool and projections and try again. If issue persists contact support.
            </Alert>
          </Snackbar>
        )
      }
      <Grid container style={{ backgroundColor: 'rgba(1, 90, 206, 0.15)'}}>
        {/* <Grid container item xs={12} justifyContent='space-between'>
          <Box>
            {updateInfo?.projectionsLastUpdated && (
              <Box
                paddingTop={0.5}
                fontSize={10}
                color={"#BCBCBC"}
                paddingRight={2}
                textAlign={"end"}
              >
                Projections Last Updated:{" "}
                {moment
                  .utc(updateInfo?.projectionsLastUpdated)
                  .local()
                  .format("M/D h:mm A")}
              </Box>
            )}
            {updateInfo?.ownershipLastUpdated && (
              <Box fontSize={10} color={"#BCBCBC"} paddingRight={2} textAlign={"end"}>
                Ownership Last Updated:{" "}
                {moment
                  .utc(updateInfo?.ownershipLastUpdated)
                  .local()
                  .format("M/D h:mm A")}
              </Box>
            )}
          </Box>
        </Grid> */}
        <Grid container item xs={12}>
          <Stack width={'100%'} direction='row' alignItems='center' display='flex' justifyContent='space-between' padding='7px 20px'>
            <Stack direction={'row'} alignItems='flex-start' display='flex' justifyContent='space-between' spacing={1} flexWrap='wrap'>
              <SettingsSelectors />
              <Box height='100%'>
                <SelectInput
                  data={percentToFirstOptions}
                  label="Percent to First"
                  bottomLabel={toolTipText.percentToFirst}
                  value={percentToFirst}
                  onSelect={(selection: any) => handlePercentChange(selection.value)}
                  tierLock={false}
                />
              </Box>
              {
                sports && sports?.length > 0 && (
                  <Box>
                    <Upload
                      inputId="lineupsInput"
                      onUpload={(lineups: string) => {
                        SetLineupFile(lineups);
                        setLineupFile(lineups);
                      }}
                      selectedSlate={selectedSlate}
                      label="Upload Lineups"
                      caption=''
                      defaultFileName={exportableLineups ? 'Contest Generator File' : ''}
                      hideDropzone
                    />
                  </Box>
                )
              }
              {
                (window.location.hostname === 'dev.tools.stokastic.com' || window.location.href.includes('localhost'))  && (
                  <Stack direction='column' sx={{
                    'input::-webkit-outer-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0,
                    },
                    'input::-webkit-inner-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0,
                    },
                    'input[type=number]': {
                      '-moz-appearance': 'textfield'
                    }
                  }}>
                    <Stack sx={{ color: 'white', opacity: '.7', fontSize: '12px', }}>Simulation Count</Stack>
                    <Input
                      placeholder='Simulation Count'
                      value={simCount}
                      aria-label='Simulation Count'
                      type='number'
                      onChange={(e) => {
                        console.log('e.target.value', e.target.value)
                        setSimCount(parseInt(e.target.value))
                        dispatch(setSimCountAction(parseInt(e.target.value)))
                      }}
                      sx={{
                        borderRadius: '6px',
                        background: '#0A0A0A',
                        height: '100%',
                        color: 'white',
                        padding: '2px',
                      }}
                    />
                  </Stack>
                )
              } 
              {/* <Stack height='100%' alignItems='center' pt={1.5}>
                <Box onClick={getLastRun} border='1px solid white' p={1} borderRadius={6} style={{ cursor: 'pointer' }}>
                  Get Last Run
                </Box>
              </Stack> */}
            </Stack>
            <Stack direction='row' alignItems='center'>
              <Button className={classes.button} endIcon={<GetAppIcon />} onClick={() => getTemplate()}>Download Projections</Button>
              <Box>
                {updateInfo?.projectionsLastUpdated && (
                  <Box
                    paddingTop={0.5}
                    fontSize={10}
                    color={"#BCBCBC"}
                    paddingRight={2}
                    textAlign={"end"}
                  >
                    Projections Last Updated:{" "}
                    {moment
                      .utc(updateInfo?.projectionsLastUpdated)
                      .local()
                      .format("M/D h:mm A")}
                  </Box>
                )}
                {updateInfo?.ownershipLastUpdated && (
                  <Box fontSize={10} color={"#BCBCBC"} paddingRight={2} textAlign={"end"}>
                    Ownership Last Updated:{" "}
                    {moment
                      .utc(updateInfo?.ownershipLastUpdated)
                      .local()
                      .format("M/D h:mm A")}
                  </Box>
                )}
              </Box>
              <EntryManager />
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </StyledSettingsContainer>
  )
};


const mapStateToProps = (state: any) => {
  const {
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    disableProgressCheck,
    supportedSites,
    totalEntries,
    lineupVerified,
    simulationTimestamp,
    showCachedResultError
  } = state.preContest;

  const {
    authenticated,
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate
  } = state.common;

  const {
    exportableLineups
  } = state.contestGenerator;

  return {
    selectedUser,
    selectedOpponent,
    filters,
    pageInfo,
    authenticated,
    disableProgressCheck,
    supportedSites,
    selectedSport,
    selectedSite,
    selectedType,
    selectedSlate,
    totalEntries,
    exportableLineups,
    lineupVerified,
    simulationTimestamp,
    showCachedResultError
  }
};

const mapDispatchToProps = {
  SetContest: setContest,
  SetAuth: setAuth,
  GetSimResults: getSimResults,
  SetDisableProgressCheck: setDisableProgressCheck,
  SetTotalEntries: setTotalEntries,
  SetSimulatedSlate: setSimulatedSlate,
  SetLineupFile: setLineupFile,
  SetPercentToFirst: setPercentToFirst,
  SetLineupVerified: setLineupVerified,
  SetShowCachedResultError: setShowCachedResultError,
  SetResultsLoadError: setResultsLoadError,
  SetIsParsingCsv: setIsParsingCsv
};

export default connect(mapStateToProps, mapDispatchToProps)(Settings);